import React, { useState, useEffect } from 'react';
import { useQuery } from 'react-query';
import { useForm } from 'react-hook-form';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { 
  Upload, 
  MapPin, 
  Calendar, 
  FileImage, 
  AlertCircle,
  Check,
  Search
} from 'lucide-react';
import { MapContainer, TileLayer, Marker, Popup, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import { apiService } from '../services/apiService';
import LoadingSpinner from '../components/UI/LoadingSpinner';
import SearchableSelect from '../components/UI/SearchableSelect';
import toast from 'react-hot-toast';

// Custom fire icon for map markers
const fireIcon = new L.Icon({
  iconUrl: 'data:image/svg+xml;base64,' + btoa(`
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="#E85D04" width="40" height="40">
      <path d="M12 2C13.1 2 14 2.9 14 4C14 5.3 13.1 6.1 12.5 6.5C12.2 6.7 12 7 12 7.3V8H13C14.1 8 15 8.9 15 10V11C15 12.1 14.1 13 13 13H11C9.9 13 9 12.1 9 11V10C9 8.9 9.9 8 11 8H12V7.3C12 6.5 12.4 5.8 13 5.3C13.6 4.8 14 4.1 14 3.3C14 2.6 13.4 2 12.7 2H12Z"/>
      <path d="M8.5 14.5C8.5 16.4 10.1 18 12 18S15.5 16.4 15.5 14.5C15.5 13.1 14.6 11.9 13.3 11.3C12.9 11.1 12.5 11 12 11S11.1 11.1 10.7 11.3C9.4 11.9 8.5 13.1 8.5 14.5Z"/>
    </svg>
  `),
  iconSize: [48, 48],
  iconAnchor: [24, 48],
  popupAnchor: [0, -48],
});

// Custom photo location marker for user-selected location
const photoLocationIcon = new L.Icon({
  iconUrl: 'data:image/svg+xml;base64,' + btoa(`
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="#1B4D3E" width="24" height="24">
      <path d="M12 15.5C13.933 15.5 15.5 13.933 15.5 12S13.933 8.5 12 8.5 8.5 10.067 8.5 12 10.067 15.5 12 15.5Z"/>
      <path d="M20 4H16.83L15 2H9L7.17 4H4C2.9 4 2 4.9 2 6V18C2 19.1 2.9 20 4 20H20C21.1 20 22 19.1 22 18V6C22 4.9 21.1 4 20 4ZM12 17C9.24 17 7 14.76 7 12S9.24 7 12 7 17 9.24 17 12 14.76 17 12 17Z"/>
    </svg>
  `),
  iconSize: [28, 28],
  iconAnchor: [14, 28],
  popupAnchor: [0, -28],
});

const LocationPicker = ({ onLocationSelect, selectedLocation }) => {
  const map = useMapEvents({
    click(e) {
      onLocationSelect(e.latlng);
    },
  });

  return selectedLocation ? (
    <Marker position={[selectedLocation.lat, selectedLocation.lng]} icon={photoLocationIcon}>
      <Popup>
        <div className="text-center">
          <p className="font-medium">📸 Localização da Foto</p>
          <p className="text-sm text-gray-600">
            {selectedLocation.lat.toFixed(6)}, {selectedLocation.lng.toFixed(6)}
          </p>
        </div>
      </Popup>
    </Marker>
  ) : null;
};

const UploadPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [filePreview, setFilePreview] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [mapCenter, setMapCenter] = useState([39.5, -8.0]);
  const [mapZoom, setMapZoom] = useState(7);

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
  } = useForm();

  const selectedFireId = watch('fire_id');

  // Pre-select fire from URL parameter
  useEffect(() => {
    const fireIdParam = searchParams.get('fire_id');
    if (fireIdParam) {
      setValue('fire_id', fireIdParam);
    }
  }, [searchParams, setValue]);

  // Fetch active fires
  const { data: firesData, isLoading: firesLoading } = useQuery(
    'active-fires',
    () => apiService.getFires({ active_only: true, limit: 100 })
  );

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Por favor selecione um arquivo de imagem');
      return;
    }

    // Validate file size (10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Arquivo muito grande. Máximo 10MB');
      return;
    }

    setSelectedFile(file);

    // Create preview
    const reader = new FileReader();
    reader.onload = (e) => {
      setFilePreview(e.target.result);
    };
    reader.readAsDataURL(file);
  };

  const handleLocationSelect = (latlng) => {
    setSelectedLocation(latlng);
    setValue('lat', latlng.lat);
    setValue('lng', latlng.lng);
  };

  // Location search functionality
  const searchLocation = async (query) => {
    if (!query || query.length < 3) {
      setSearchResults([]);
      setShowSearchResults(false);
      return;
    }

    setIsSearching(true);
    try {
      // Use Nominatim geocoding service (free, no API key required)
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}&countrycodes=pt&limit=5&addressdetails=1`
      );
      const data = await response.json();
      
      setSearchResults(data);
      setShowSearchResults(true);
    } catch (error) {
      console.error('Error searching location:', error);
      toast.error('Erro ao pesquisar localização');
    } finally {
      setIsSearching(false);
    }
  };

  const handleSearchInputChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    
    // Debounce search
    clearTimeout(window.searchTimeout);
    window.searchTimeout = setTimeout(() => {
      searchLocation(query);
    }, 500);
  };

  const selectSearchResult = (result) => {
    const lat = parseFloat(result.lat);
    const lng = parseFloat(result.lon);
    
    // Update map center and zoom
    setMapCenter([lat, lng]);
    setMapZoom(15);
    
    // Set as selected location
    handleLocationSelect({ lat, lng });
    
    // Update search UI
    setSearchQuery(result.display_name);
    setShowSearchResults(false);
    
    toast.success('Localização selecionada!');
  };

  const onSubmit = async (data) => {
    if (!selectedFile) {
      toast.error('Por favor selecione uma foto');
      return;
    }

    if (!selectedLocation) {
      toast.error('Por favor selecione a localização no mapa');
      return;
    }

    try {
      setIsUploading(true);

      const formData = new FormData();
      formData.append('photo', selectedFile);
      formData.append('fire_id', data.fire_id);
      formData.append('lat', selectedLocation.lat);
      formData.append('lng', selectedLocation.lng);
      formData.append('taken_at', data.taken_at);
      if (data.description) {
        formData.append('description', data.description);
      }
      formData.append('is_private', data.is_private || false);
      if (data.source) {
        formData.append('source', data.source);
      }
      if (data.original_url) {
        formData.append('original_url', data.original_url);
      }

      await apiService.uploadPhoto(formData);
      
      toast.success('Foto enviada com sucesso!');
      navigate('/photos?user=me');
      
    } catch (error) {
      const message = error.response?.data?.error || 'Erro ao enviar foto';
      toast.error(message);
    } finally {
      setIsUploading(false);
    }
  };

  const selectedFire = firesData?.fires?.find(f => f.id === parseInt(selectedFireId));

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
          Enviar Foto
        </h1>
        <p className="text-gray-600 dark:text-gray-400">
          Partilhe uma foto de um incêndio florestal
        </p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left Column - Form */}
          <div className="space-y-6">
            {/* Fire Selection */}
            <div className="card p-6">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                1. Selecionar Incêndio
              </h2>
              
              <div>
                <label className="form-label">
                  Incêndio *
                </label>
                <SearchableSelect
                  options={firesData?.fires?.map(fire => ({
                    value: fire.id,
                    label: `${fire.location} - ${fire.district}`,
                    fire: fire
                  })) || []}
                  value={selectedFireId}
                  onChange={(value) => setValue('fire_id', value)}
                  placeholder="Selecione um incêndio..."
                  searchPlaceholder="Pesquisar incêndios..."
                  isLoading={firesLoading}
                  required={true}
                  renderOption={(option) => (
                    <div className="flex items-start space-x-3">
                      <div className="w-3 h-3 bg-red-500 rounded-full mt-1 flex-shrink-0"></div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                          {option.fire.location}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                          {option.fire.district} • {option.fire.status}
                        </p>
                      </div>
                    </div>
                  )}
                />
                {errors.fire_id && (
                  <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                    {errors.fire_id.message}
                  </p>
                )}
                
                {selectedFire && (
                  <div className="mt-3 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                    <p className="text-sm font-medium text-gray-900 dark:text-white">
                      {selectedFire.location}
                    </p>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {selectedFire.district} • {selectedFire.status}
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Photo Upload */}
            <div className="card p-6">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                2. Selecionar Foto
              </h2>
              
              <div className="space-y-4">
                <div>
                  <label className="form-label">
                    Arquivo de imagem *
                  </label>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleFileSelect}
                    className="form-input"
                  />
                  <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                    Formatos aceites: JPG, PNG. Máximo 10MB.
                  </p>
                </div>

                {filePreview && (
                  <div className="relative">
                    <img
                      src={filePreview}
                      alt="Preview"
                      className="w-full h-48 object-cover rounded-lg"
                    />
                    <div className="absolute top-2 right-2 bg-green-500 text-white p-1 rounded-full">
                      <Check className="w-4 h-4" />
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Date and Time */}
            <div className="card p-6">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                3. Data e Hora
              </h2>
              
              <div>
                <label className="form-label">
                  Quando foi tirada a foto? *
                </label>
                <input
                  type="datetime-local"
                  className="form-input"
                  {...register('taken_at', { required: 'Data e hora são obrigatórias' })}
                />
                {errors.taken_at && (
                  <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                    {errors.taken_at.message}
                  </p>
                )}
              </div>
            </div>

            {/* Description */}
            <div className="card p-6">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                4. Descrição (Opcional)
              </h2>
              
              <div>
                <label className="form-label">
                  Descrição da foto
                </label>
                <textarea
                  rows={3}
                  className="form-input"
                  placeholder="Descreva o que vê na foto..."
                  {...register('description')}
                />
              </div>
            </div>

            {/* Additional Information */}
            <div className="card p-6">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                5. Informações Adicionais
              </h2>
              
              <div className="space-y-4">
                {/* Private checkbox */}
                <div className="flex items-center space-x-3">
                  <input
                    type="checkbox"
                    id="is_private"
                    className="w-4 h-4 text-primary-600 bg-gray-100 border-gray-300 rounded focus:ring-primary-500 dark:focus:ring-primary-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                    {...register('is_private')}
                  />
                  <label htmlFor="is_private" className="text-sm font-medium text-gray-900 dark:text-white">
                    Privado
                  </label>
                  <span className="text-sm text-gray-500 dark:text-gray-400">
                    (Foto apenas visível para administradores)
                  </span>
                </div>

                {/* Source field */}
                <div>
                  <label className="form-label">
                    Fonte
                  </label>
                  <input
                    type="text"
                    className="form-input"
                    placeholder="Ex: Bombeiros Voluntários de Lisboa, João Silva, etc."
                    {...register('source')}
                  />
                  <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                    Indique a fonte ou origem da imagem
                  </p>
                </div>

                {/* Original URL field */}
                <div>
                  <label className="form-label">
                    Link para o original (Opcional)
                  </label>
                  <input
                    type="url"
                    className="form-input"
                    placeholder="https://exemplo.com/foto-original"
                    {...register('original_url', {
                      pattern: {
                        value: /^https?:\/\/.+/,
                        message: 'Por favor, insira um URL válido (começando com http:// ou https://)'
                      }
                    })}
                  />
                  {errors.original_url && (
                    <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                      {errors.original_url.message}
                    </p>
                  )}
                  <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                    Link para a fonte original da imagem (se aplicável)
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column - Map */}
          <div className="space-y-6">
            <div className="card p-6">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                6. Localização no Mapa
              </h2>
              
              <div className="space-y-4">
                <div className="flex items-center space-x-2 text-sm text-gray-600 dark:text-gray-400">
                  <AlertCircle className="w-4 h-4" />
                  <span>Pesquise uma localização ou clique no mapa onde a foto foi tirada</span>
                </div>

                {/* Location Search Box */}
                <div className="relative">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Pesquisar localização (ex: Rua da Liberdade, Lisboa)"
                      value={searchQuery}
                      onChange={handleSearchInputChange}
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                    {isSearching && (
                      <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-600"></div>
                      </div>
                    )}
                  </div>

                  {/* Search Results Dropdown */}
                  {showSearchResults && searchResults.length > 0 && (
                    <div className="absolute z-10 w-full mt-1 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                      {searchResults.map((result, index) => (
                        <button
                          key={index}
                          onClick={() => selectSearchResult(result)}
                          className="w-full px-4 py-3 text-left hover:bg-gray-50 dark:hover:bg-gray-600 border-b border-gray-200 dark:border-gray-600 last:border-b-0 focus:outline-none focus:bg-gray-50 dark:focus:bg-gray-600"
                        >
                          <div className="flex items-start space-x-2">
                            <MapPin className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                                {result.display_name.split(',')[0]}
                              </p>
                              <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                                {result.display_name}
                              </p>
                            </div>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}

                  {/* No Results Message */}
                  {showSearchResults && searchResults.length === 0 && searchQuery.length >= 3 && !isSearching && (
                    <div className="absolute z-10 w-full mt-1 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg shadow-lg p-4">
                      <p className="text-sm text-gray-500 dark:text-gray-400 text-center">
                        Nenhuma localização encontrada para "{searchQuery}"
                      </p>
                    </div>
                  )}
                </div>

                <div className="h-64 rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700">
                  <MapContainer
                    center={selectedFire ? [selectedFire.lat, selectedFire.lng] : mapCenter}
                    zoom={selectedFire ? 12 : mapZoom}
                    style={{ height: '100%', width: '100%' }}
                    key={`${mapCenter[0]}-${mapCenter[1]}-${mapZoom}`}
                  >
                    <TileLayer
                      url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                      attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                    />
                    <LocationPicker
                      onLocationSelect={handleLocationSelect}
                      selectedLocation={selectedLocation}
                    />
                    {selectedFire && (
                      <Marker 
                        position={[selectedFire.lat, selectedFire.lng]}
                        icon={fireIcon}
                      >
                        <Popup>
                          <div className="text-center">
                            <p className="font-medium">{selectedFire.location}</p>
                            <p className="text-sm text-gray-600 dark:text-gray-400">
                              Status: <span className="font-medium">{selectedFire.status}</span>
                            </p>
                            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                              Clique no mapa para marcar onde tirou a foto
                            </p>
                          </div>
                        </Popup>
                      </Marker>
                    )}
                  </MapContainer>
                </div>

                {selectedLocation && (
                  <div className="p-3 bg-green-50 dark:bg-green-900/20 rounded-lg">
                    <div className="flex items-center space-x-2 text-green-700 dark:text-green-300">
                      <MapPin className="w-4 h-4" />
                      <span className="text-sm font-medium text-green-700 dark:text-green-300">
                        Localização selecionada
                      </span>
                    </div>
                    <p className="text-sm text-green-600 dark:text-green-400 mt-1">
                      {selectedLocation.lat.toFixed(6)}, {selectedLocation.lng.toFixed(6)}
                    </p>
                  </div>
                )}

                {/* Hidden inputs for coordinates */}
                <input type="hidden" {...register('lat')} />
                <input type="hidden" {...register('lng')} />
              </div>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isUploading || !selectedFile || !selectedLocation}
              className="w-full btn-primary flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isUploading ? (
                <>
                  <LoadingSpinner size="sm" />
                  <span>A enviar...</span>
                </>
              ) : (
                <>
                  <Upload className="w-5 h-5" />
                  <span>Enviar Foto</span>
                </>
              )}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default UploadPage;
